/**
 * BackfillVendorsModal — wizard for filling in placeholder vendor_dispatch
 * actions across draft workflows.
 *
 * For each unconfigured ref we surface up to 3 fuzzy-matched candidates
 * (token-overlap score against the step name). When no candidate clears
 * threshold, the row falls back to a full manual picker built from the
 * approved vendor_services catalog.
 *
 * The admin can pick / skip per row; on submit we POST the accepted set
 * to /admin/wfe/vendor-coverage/apply and refresh the coverage map.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { CheckCircle2, Sparkles, X, AlertTriangle } from "lucide-react";

function confidenceBadge(conf) {
  if (conf >= 0.5) return { cls: "bg-emerald-100 text-emerald-700", label: "high" };
  if (conf >= 0.2) return { cls: "bg-amber-100 text-amber-700",     label: "medium" };
  if (conf > 0)    return { cls: "bg-slate-100 text-slate-600",     label: "low" };
  return null;
}

function SuggestionRow({ row, services, picked, onPick }) {
  const [manualMode, setManualMode] = useState(row.candidates.length === 0);
  const approved = services.filter((s) => s.status === "approved");
  const byVendor = useMemo(() => {
    const out = new Map();
    approved.forEach((s) => {
      if (!s.vendor_id) return;
      if (!out.has(s.vendor_id)) out.set(s.vendor_id, { vendor_name: s.vendor_name, services: [] });
      out.get(s.vendor_id).services.push(s);
    });
    return Array.from(out.entries()).sort((a, b) =>
      (a[1].vendor_name || "").localeCompare(b[1].vendor_name || ""),
    );
  }, [approved]);

  return (
    <div
      data-testid={`backfill-row-${row.action_id}`}
      className={`rounded-xl border p-3 ${picked ? "border-emerald-300 bg-emerald-50/60" : "border-slate-200 bg-white"}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 flex-wrap">
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
              {row.definition_name} <span className="font-mono text-slate-400">v{row.definition_version} · {row.definition_state || "draft"}</span>
            </div>
            {row.requires_clone && (
              <span
                data-testid={`backfill-row-clone-badge-${row.action_id}`}
                className="inline-flex items-center gap-0.5 rounded-full bg-blue-100 text-blue-700 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider"
                title="A new draft version will be created when you apply this pick — the live workflow stays untouched."
              >
                will clone
              </span>
            )}
          </div>
          <div className="text-sm font-semibold text-slate-900 mt-0.5">{row.step_name}</div>
          <div className="text-[10px] text-slate-500">stage: {row.stage_name}</div>
        </div>
        {picked && (
          <span
            data-testid={`backfill-row-picked-${row.action_id}`}
            className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-emerald-600 text-white rounded-full px-2 py-0.5"
          >
            <CheckCircle2 className="w-3 h-3" /> picked
          </span>
        )}
      </div>

      {!manualMode && row.candidates.length > 0 && (
        <div className="mt-2 space-y-1">
          {row.candidates.map((c) => {
            const isPicked = picked && picked.service_id === c.service_id;
            const conf = confidenceBadge(c.confidence);
            return (
              <button
                key={c.service_id}
                type="button"
                data-testid={`backfill-candidate-${row.action_id}-${c.service_id}`}
                onClick={() => onPick(isPicked ? null : c)}
                className={`w-full flex items-center justify-between gap-2 rounded-lg border px-3 py-2 text-left text-xs transition-colors ${
                  isPicked
                    ? "border-emerald-500 bg-emerald-50"
                    : "border-slate-200 bg-white hover:border-blue-300"
                }`}
              >
                <span className="min-w-0">
                  <span className="font-semibold text-slate-900">{c.vendor_name}</span>
                  <span className="text-slate-500"> · {c.service_name}</span>
                </span>
                {conf && (
                  <span className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider ${conf.cls}`}>
                    {conf.label} {(c.confidence * 100).toFixed(0)}%
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {(manualMode || row.candidates.length === 0) && (
        <div className="mt-2 rounded-lg border border-slate-200 bg-slate-50 p-2">
          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">
            Pick manually
          </div>
          <select
            data-testid={`backfill-manual-${row.action_id}`}
            value={picked?.service_id || ""}
            onChange={(e) => {
              const id = e.target.value;
              if (!id) return onPick(null);
              const svc = approved.find((s) => s.id === id);
              if (!svc) return;
              onPick({
                service_id: svc.id,
                service_name: svc.name,
                vendor_id: svc.vendor_id,
                vendor_name: svc.vendor_name,
                custom_link: svc.custom_link,
                custom_link_reason: svc.custom_link_reason,
                confidence: 0,
              });
            }}
            className="w-full text-xs border border-slate-300 rounded px-2 py-1.5 bg-white"
          >
            <option value="">— Skip this ref —</option>
            {byVendor.map(([vid, v]) => (
              <optgroup key={vid} label={v.vendor_name || "(unknown vendor)"}>
                {v.services.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>
      )}

      {!manualMode && row.candidates.length > 0 && (
        <button
          type="button"
          onClick={() => setManualMode(true)}
          className="mt-2 text-[10px] text-blue-600 hover:underline"
        >
          None of these — pick manually
        </button>
      )}
    </div>
  );
}

export default function BackfillVendorsModal({ open, onClose, onApplied }) {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [nonDraftSkipped, setNonDraftSkipped] = useState(0);
  const [includeClones, setIncludeClones] = useState(false);
  const [services, setServices] = useState([]);
  // Keyed by action_id → chosen vendor service object (or undefined to skip).
  const [picks, setPicks] = useState({});

  const loadSuggestions = async (withClones) => {
    setLoading(true);
    try {
      const [s, v] = await Promise.all([
        api.get("/admin/wfe/vendor-coverage/suggestions", { params: { include_clones: withClones } }),
        api.get("/admin/vendor-services"),
      ]);
      setSuggestions(s.data?.suggestions || []);
      setNonDraftSkipped(s.data?.non_draft_skipped || 0);
      setServices(Array.isArray(v.data) ? v.data : []);
      setPicks({});
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) loadSuggestions(includeClones);
  }, [open, includeClones]);

  const pickedCount = Object.values(picks).filter(Boolean).length;

  const acceptAllHighConfidence = () => {
    const next = { ...picks };
    suggestions.forEach((row) => {
      const top = row.candidates[0];
      if (top && top.confidence >= 0.5 && !next[row.action_id]) next[row.action_id] = top;
    });
    setPicks(next);
  };

  const handleSubmit = async () => {
    const items = suggestions
      .filter((row) => picks[row.action_id])
      .map((row) => ({
        definition_id: row.definition_id,
        stage_id: row.stage_id,
        step_id: row.step_id,
        action_id: row.action_id,
        service_id: picks[row.action_id].service_id,
      }));
    if (items.length === 0) {
      toast.error("Pick at least one ref to backfill.");
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post("/admin/wfe/vendor-coverage/apply", { items });
      const applied = r.data?.applied || 0;
      const clones = r.data?.clones_created || [];
      if (clones.length > 0) {
        toast.success(
          `Applied ${applied}/${r.data?.total || 0} refs · ${clones.length} workflow${clones.length === 1 ? "" : "s"} cloned into draft`,
        );
      } else {
        toast.success(`Applied ${applied}/${r.data?.total || 0} refs`);
      }
      onApplied?.();
      onClose?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="backfill-modal">
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" />
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-3xl max-h-[88vh] flex flex-col rounded-2xl bg-white border border-slate-200 shadow-2xl"
      >
        <div className="px-6 py-4 border-b border-slate-100 flex items-start justify-between gap-3">
          <div>
            <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] font-bold text-blue-600">
              <Sparkles className="w-3 h-3" /> Backfill placeholders
            </div>
            <h2 className="mt-1 font-display text-xl font-bold text-slate-900">
              Wire up unconfigured vendor dispatches
            </h2>
            <p className="mt-1 text-xs text-slate-500">
              We scan every <strong>draft</strong> workflow, fuzzy-match each unconfigured{" "}
              <code className="bg-slate-100 px-1 rounded text-[10px]">vendor_dispatch</code> action to
              an approved vendor service, and let you accept / pick manually / skip per ref.
            </p>
            {!includeClones && nonDraftSkipped > 0 && (
              <div
                data-testid="backfill-non-draft-warning"
                className="mt-2 flex items-start gap-2 flex-wrap text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5"
              >
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <span className="flex-1">
                  <strong>{nonDraftSkipped}</strong> ref{nonDraftSkipped === 1 ? "" : "s"} on live / paused workflows skipped.
                </span>
                <button
                  type="button"
                  onClick={() => setIncludeClones(true)}
                  data-testid="backfill-include-clones"
                  className="text-[11px] font-bold text-blue-700 hover:underline"
                >
                  Include them (clone &amp; wire) →
                </button>
              </div>
            )}
            {includeClones && (
              <div className="mt-2 inline-flex items-start gap-1.5 text-[11px] text-blue-800 bg-blue-50 border border-blue-200 rounded-md px-2 py-1.5">
                <Sparkles className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <span>
                  <strong>Clone &amp; wire mode on.</strong> Picks on live / paused workflows will create new draft versions you can review before deploying.
                </span>
                <button
                  type="button"
                  onClick={() => setIncludeClones(false)}
                  data-testid="backfill-disable-clones"
                  className="ml-1 text-[11px] font-bold text-blue-700 hover:underline"
                >
                  drafts only
                </button>
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="backfill-close"
            className="text-slate-400 hover:text-slate-700 p-1"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-2">
          {loading ? (
            <div className="text-sm text-slate-500">Scanning workflows…</div>
          ) : suggestions.length === 0 ? (
            <div data-testid="backfill-empty" className="text-center py-12 text-sm text-slate-500">
              <CheckCircle2 className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
              All draft workflows are fully wired. Nothing to backfill.
            </div>
          ) : (
            suggestions.map((row) => (
              <SuggestionRow
                key={row.action_id}
                row={row}
                services={services}
                picked={picks[row.action_id]}
                onPick={(svc) => setPicks((p) => ({ ...p, [row.action_id]: svc }))}
              />
            ))
          )}
        </div>

        {suggestions.length > 0 && (
          <div className="px-6 py-3 border-t border-slate-100 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap">
              <button
                type="button"
                onClick={acceptAllHighConfidence}
                data-testid="backfill-accept-all-high"
                className="text-xs font-semibold text-blue-700 hover:underline"
              >
                Accept all high-confidence
              </button>
              <span className="text-xs text-slate-500" data-testid="backfill-pick-count">
                {pickedCount} of {suggestions.length} ref{suggestions.length === 1 ? "" : "s"} selected
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="text-sm font-semibold text-slate-700 px-3 py-2 rounded-full hover:bg-slate-100"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={submitting || pickedCount === 0}
                data-testid="backfill-submit"
                className="inline-flex items-center gap-1.5 bg-slate-900 hover:bg-slate-800 text-white rounded-full px-4 py-2 text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Sparkles className="w-3.5 h-3.5" />
                {submitting ? "Applying…" : `Apply ${pickedCount} pick${pickedCount === 1 ? "" : "s"}`}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
